import type { ModuleManifest } from './types.js';
import { CORE_MODULE_ID } from './types.js';

export { CORE_MODULE_ID };

export const coreManifest: ModuleManifest = {
  id: CORE_MODULE_ID,
  kind: 'core',
  displayName: 'Core',
  description: 'Dashboards, AI Agents, Admin, Marketplace, and account settings',
  icon: 'LayoutDashboard',
  navOrder: 0,
  status: 'active',
  policies: [],
  dependsOn: [],
  nav: [
    // Dashboards group (no policy)
    { path: '/', label: 'Dashboard', icon: 'LayoutDashboard', group: 'Dashboards', order: 10 },
    { path: '/dashboards/new', label: 'New Dashboard', icon: 'PlusCircle', group: 'Dashboards', order: 11 },

    // AI Agents group (policy: CanViewAgents)
    {
      path: '/agents/workflows',
      label: 'Workflows',
      icon: 'Workflow',
      group: 'AI Agents',
      policy: 'CanViewAgents',
      order: 21,
    },
    {
      path: '/agents/libraries',
      label: 'Libraries',
      icon: 'Library',
      group: 'AI Agents',
      policy: 'CanViewAgents',
      order: 22,
    },
    {
      path: '/agents/decisions',
      label: 'Action Queue',
      icon: 'Gavel',
      group: 'AI Agents',
      policy: 'CanViewAgents',
      order: 23,
    },
    {
      path: '/workforce',
      label: 'Workforce',
      icon: 'Users',
      group: 'AI Agents',
      policy: 'CanManageWorkforce',
      order: 29,
    },

    // AI Runtime group: operational views for user-facing workflow/workforce execution.
    {
      path: '/agents/active',
      label: 'Runtime',
      icon: 'Activity',
      group: 'AI Runtime',
      policy: 'CanViewAgents',
      order: 10,
    },
    {
      path: '/agents/runs',
      label: 'Run History',
      icon: 'Activity',
      group: 'AI Runtime',
      policy: 'CanViewAgents',
      order: 20,
    },

    // Workspace group (Spec 59 + 61 + 57b) — above Knowledge
    {
      path: '/projects',
      label: 'Projects',
      icon: 'Briefcase',
      group: 'Workspace',
      policy: 'CanViewProjects',
      order: 38,
    },
    {
      path: '/tasks',
      label: 'Tasks',
      icon: 'ListTodo',
      group: 'Workspace',
      policy: 'CanManageTasks',
      order: 39,
    },
    {
      // Spec 57b: Conversations live in Workspace, not AI Agents. The list
      // is user-initiated only — assistant-ui threads are filtered server-side.
      path: '/conversations',
      label: 'Conversations',
      icon: 'MessageSquare',
      group: 'Workspace',
      policy: 'CanViewAgents',
      order: 40,
    },

    // Knowledge group (Spec 58)
    {
      path: '/memories',
      label: 'Memories',
      icon: 'Brain',
      group: 'Knowledge',
      policy: 'CanViewMemoriesArtifacts',
      order: 41,
    },
    {
      path: '/artifacts',
      label: 'Artifacts',
      icon: 'FileText',
      group: 'Knowledge',
      policy: 'CanViewMemoriesArtifacts',
      order: 42,
    },
    {
      path: '/agents/intel-sources',
      label: 'Intel Sources',
      icon: 'Radio',
      group: 'Knowledge',
      policy: 'CanViewAgents',
      order: 43,
    },

    // Admin group (system organization only)
    {
      path: '/admin/organizations',
      label: 'Organizations',
      icon: 'Building2',
      group: 'Admin',
      policy: 'RequireSuperadmin',
      order: 33,
    },
    { path: '/admin/system', label: 'System', icon: 'Server', group: 'Admin', policy: 'RequireSuperadmin', order: 34 },
    {
      path: '/admin/runtime-flags',
      label: 'Runtime Flags',
      icon: 'ToggleRight',
      group: 'Admin',
      policy: 'CanManageOrgRuntimeFlags',
      order: 35,
    },
    { path: '/admin/daemons', label: 'Daemons', icon: 'Bot', group: 'Admin', policy: 'RequireSuperadmin', order: 38 },
    {
      path: '/admin/run-monitoring',
      label: 'Run Health',
      icon: 'Activity',
      group: 'Admin',
      policy: 'CanManageWorkflows',
      order: 38.5,
    },
    {
      path: '/admin/quality',
      label: 'Quality',
      icon: 'TrendingUp',
      group: 'Admin',
      policy: 'RequireSuperadmin',
      order: 39,
    },
    {
      path: '/admin/evaluations',
      label: 'Evaluations',
      icon: 'FlaskConical',
      group: 'Admin',
      policy: 'RequireSuperadmin',
      order: 40,
    },
    {
      path: '/admin/evaluation-templates',
      label: 'Eval Templates',
      icon: 'ClipboardList',
      group: 'Admin',
      policy: 'CanManageEvaluationTemplates',
      order: 41,
    },
    {
      path: '/admin/improvements',
      label: 'Improvements',
      icon: 'Brain',
      group: 'Admin',
      policy: 'CanViewImprovements',
      order: 42,
    },

    // Organization group (mixed policies — items appear only for the user's role)
    {
      path: '/organization/users',
      label: 'Users',
      icon: 'Users',
      group: 'Organization',
      policy: 'CanManageOrgMembers',
      order: 50,
    },
    {
      path: '/organization/settings',
      label: 'Org Settings',
      icon: 'Settings',
      group: 'Organization',
      policy: 'CanViewOrg',
      order: 51,
    },
    {
      path: '/organization/api-keys',
      label: 'API Keys',
      icon: 'Key',
      group: 'Organization',
      policy: 'CanManageWorkerSdkKeys',
      order: 52,
    },
    {
      path: '/organization/platforms',
      label: 'Platforms',
      icon: 'Plug',
      group: 'Organization',
      policy: 'CanManageIntegrations',
      order: 53,
    },
    {
      path: '/organization/mcp-connections',
      label: 'MCP Connections',
      icon: 'PlugZap',
      group: 'Organization',
      policy: 'CanManageIntegrations',
      order: 54,
    },

    // Help group (policy: CanViewInternalDocs)
    {
      path: '/help/system-overview',
      label: 'System Overview',
      icon: 'Map',
      group: 'Help',
      policy: 'CanViewInternalDocs',
      order: 61,
    },
    {
      path: '/help/entity-summaries',
      label: 'Entity Summaries',
      icon: 'BookOpen',
      group: 'Help',
      policy: 'CanViewInternalDocs',
      order: 62,
    },

    // Marketplace group (no policy)
    { path: '/marketplace', label: 'Browse', icon: 'Store', group: 'Marketplace', order: 71 },
    { path: '/marketplace/publish', label: 'Publish', icon: 'Upload', group: 'Marketplace', order: 72 },

    // Account group (no policy)
    { path: '/settings', label: 'Settings', icon: 'Settings', group: 'Account', order: 81 },
  ],
};
